import React, { useEffect, useRef } from 'react';
import { Cpu, Battery, Wifi, Zap } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

const TechFeature: React.FC<{ icon: React.ReactNode; title: string; description: string; index: number }> = ({ 
  icon, 
  title, 
  description, 
  index 
}) => {
  const featureRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = featureRef.current;
    const iconElement = iconRef.current;

    if (!element) return;

    // Initial state
    gsap.set(element, {
      opacity: 0,
      y: 60,
      scale: 0.8
    });

    gsap.set(iconElement, {
      rotation: -180,
      scale: 0
    });

    // Scroll-triggered animation
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: element,
        start: "top 80%",
        end: "bottom 20%",
        toggleActions: "play none none reverse"
      }
    });

    // Animate the card
    tl.to(element, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.8,
      ease: "back.out(1.7)",
      delay: index * 0.2
    });

    // Animate the icon
    tl.to(iconElement, {
      rotation: 0,
      scale: 1,
      duration: 0.6,
      ease: "back.out(1.7)"
    }, "-=0.4");

    // Add hover animation
    element.addEventListener('mouseenter', () => {
      gsap.to(element, {
        y: -10,
        scale: 1.02,
        duration: 0.3,
        ease: "power2.out"
      });
      gsap.to(iconElement, {
        rotation: 10,
        duration: 0.3,
        ease: "power2.out"
      });
    });

    element.addEventListener('mouseleave', () => {
      gsap.to(element, {
        y: 0,
        scale: 1,
        duration: 0.3,
        ease: "power2.out"
      });
      gsap.to(iconElement, {
        rotation: 0,
        duration: 0.3,
        ease: "power2.out"
      });
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [index]);

  return (
    <div 
      ref={featureRef}
      className="relative cursor-pointer"
    >
      <div className="bg-gradient-to-br from-[#CC0000]/10 to-[#CC0000]/5 rounded-2xl p-6 h-full backdrop-blur-sm border border-[#CC0000]/20 hover:border-[#CC0000]/40 transition-colors duration-300">
        <div ref={iconRef} className="text-[#CC0000] mb-4">
          {icon}
        </div>
        <h3 className="text-xl font-semibold text-[#1A1A1A] mb-3">{title}</h3>
        <p className="text-gray-600">{description}</p>
      </div>
    </div>
  );
};

const Technology: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const title = titleRef.current;
    const subtitle = subtitleRef.current;

    if (!section || !title || !subtitle) return;

    // Initial state
    gsap.set([title, subtitle], {
      opacity: 0,
      y: 30
    });

    // Scroll-triggered animations
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top 70%",
        end: "bottom 30%",
        toggleActions: "play none none reverse"
      }
    });

    tl.to(title, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: "power2.out"
    })
    .to(subtitle, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: "power2.out"
    }, "-=0.4");

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const technologies = [
    {
      icon: <Cpu size={32} />,
      title: "Smart IoT Integration",
      description: "Advanced IoT connectivity enabling real-time monitoring, fleet management, and predictive maintenance for optimal performance.",
    },
    {
      icon: <Battery size={32} />,
      title: "Advanced Battery Tech",
      description: "Cutting-edge lithium-ion battery technology offering extended range, fast charging, and long-lasting performance.",
    },
    {
      icon: <Wifi size={32} />,
      title: "Connected Mobility",
      description: "Seamless connectivity features including GPS tracking, mobile app integration, and cloud-based analytics.",
    },
    {
      icon: <Zap size={32} />,
      title: "Efficient Power System",
      description: "Intelligent power management system maximizing efficiency while delivering consistent performance across all conditions.",
    }
  ];

  return (
    <section ref={sectionRef} className="relative py-20 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-white via-[#F5F5F5] to-white"></div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-12">
          <h2 ref={titleRef} className="text-3xl font-bold text-[#1A1A1A] mb-4">Innovative Technology</h2>
          <p ref={subtitleRef} className="text-gray-600 max-w-2xl mx-auto">
            Experience the future of electric mobility with our advanced technology suite, 
            designed to enhance efficiency, reliability, and user experience in last mile transportation.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {technologies.map((tech, index) => (
            <TechFeature 
              key={index}
              icon={tech.icon}
              title={tech.title}
              description={tech.description}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Technology;